"use client";

/**
 * Google Calendar booking UI — custom booker matching the Cal.com embed flow.
 *
 * Uses the same embed shell as Calendly/Cal.com, then steps inside the panel:
 *   1. Select a date
 *   2. Pick a time (back)
 *   3. Enter details + confirm (back)
 */

import { trpc } from "@plura/shared/trpc";
import { FormJourneyItem, SchedulerSettings } from "@plura/shared/types";
import { cn } from "@plura/shared/utils";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import Icon from "../../../../atoms/Icon";
import BookingConfirmationCard from "./BookingConfirmationCard";
import EmbedLoadingSkeleton from "./EmbedLoadingSkeleton";

interface Props {
  step: FormJourneyItem;
  editorMode: boolean;
  previewMode: boolean;
  active: boolean;
  handleAnswer?: () => void;
}

type BookingResponseValue = {
  startTime: string;
  endTime: string;
  inviteeName: string;
  inviteeEmail: string;
  externalId: string;
  externalUrl: string | null;
  meetUrl: string | null;
};

type BookerView = "date" | "time" | "details";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const toYmd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

function buildCalendarGrid(visibleMonth: Date): Date[] {
  const first = startOfMonth(visibleMonth);
  const startWeekday = first.getDay();
  const gridStart = new Date(first.getFullYear(), first.getMonth(), 1 - startWeekday);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    return d;
  });
}

function recallInviteeValue(
  formValues: Record<string, unknown>,
  blockId: string | null | undefined,
  kind: "name" | "email"
): string {
  if (!blockId) return "";
  const direct = formValues[blockId];
  if (typeof direct === "string" && direct.trim().length > 0) return direct.trim();
  if (kind === "name") {
    const first = formValues[`${blockId}_first_name`];
    const last = formValues[`${blockId}_last_name`];
    const composite = [first, last].filter((v) => typeof v === "string" && v.trim().length > 0).join(" ");
    if (composite.trim().length > 0) return composite.trim();
  } else {
    const email = formValues[`${blockId}_email`];
    if (typeof email === "string" && email.trim().length > 0) return email.trim();
  }
  return "";
}

function formatSlotTime(iso: string) {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function formatDateHeading(d: Date) {
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function formatTimezoneLabel(timezone: string) {
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      timeZoneName: "shortGeneric",
    }).formatToParts(new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value ?? timezone;
  } catch {
    return timezone;
  }
}

function buildPreviewSlotsByDay(
  visibleMonth: Date,
  durationMinutes: number,
): Map<string, { startTime: string; endTime: string }[]> {
  const map = new Map<string, { startTime: string; endTime: string }[]>();
  const today = startOfDay(new Date());
  const last = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0);

  for (let day = 1; day <= last.getDate(); day += 1) {
    const d = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), day);
    if (d < today) continue;
    const dow = d.getDay();
    if (dow === 0 || dow === 6) continue;

    const slots = [9, 10, 14, 15].map((hour) => {
      const start = new Date(d);
      start.setHours(hour, 0, 0, 0);
      const end = new Date(start);
      end.setMinutes(end.getMinutes() + durationMinutes);
      return { startTime: start.toISOString(), endTime: end.toISOString() };
    });
    map.set(toYmd(d), slots);
  }

  return map;
}

const CustomCalendarSlot = ({ step, editorMode, previewMode, active, handleAnswer }: Props) => {
  const form = useFormContext();
  const { conversationId, ...userResponses } = form.getValues();

  const settings = useMemo<SchedulerSettings | null>(() => {
    const parsed = SchedulerSettings.safeParse(step.schedulerSettings);
    if (parsed.success) return parsed.data;
    if (previewMode) {
      return {
        provider: "GOOGLE_CALENDAR",
        connectionId: "preview",
        calendarId: "preview",
        calendarName: "Work Calendar",
        durationMinutes: 30,
        eventTitle: "Intro call",
      };
    }
    return null;
  }, [previewMode, step.schedulerSettings]);

  const durationMinutes = settings?.durationMinutes ?? 60;
  const eventTitle = settings?.eventTitle?.trim() || "Meeting";

  const timezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return "UTC";
    }
  }, []);

  const [view, setView] = useState<BookerView>("date");
  const [visibleMonth, setVisibleMonth] = useState<Date>(() => startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<{ startTime: string; endTime: string } | null>(null);
  const [inviteeName, setInviteeName] = useState("");
  const [inviteeEmail, setInviteeEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [booking, setBooking] = useState<BookingResponseValue | null>(() => {
    const existing = userResponses[step.blockId];
    if (typeof existing !== "string" || existing.length === 0) return null;
    try {
      const parsed = JSON.parse(existing) as Partial<BookingResponseValue>;
      if (parsed.startTime && parsed.endTime && parsed.externalId) {
        return {
          startTime: parsed.startTime,
          endTime: parsed.endTime,
          inviteeName: parsed.inviteeName ?? "",
          inviteeEmail: parsed.inviteeEmail ?? "",
          externalId: parsed.externalId,
          externalUrl: parsed.externalUrl ?? null,
          meetUrl: parsed.meetUrl ?? null,
        };
      }
    } catch {
      // ignore
    }
    return null;
  });

  useEffect(() => {
    if (!settings) return;
    if (!inviteeName) {
      const recalled = recallInviteeValue(userResponses, settings.inviteeNameBlockId, "name");
      if (recalled) setInviteeName(recalled);
    }
    if (!inviteeEmail) {
      const recalled = recallInviteeValue(userResponses, settings.inviteeEmailBlockId, "email");
      if (recalled) setInviteeEmail(recalled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.inviteeNameBlockId, settings?.inviteeEmailBlockId]);

  useEffect(() => {
    form.register(step.blockId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.blockId]);

  const isRuntime = !editorMode || (editorMode && previewMode);
  const isPreviewDemo = previewMode;
  const canFetch = !!settings && !isPreviewDemo && (active || editorMode);

  const slotRange = useMemo(() => {
    const first = startOfMonth(visibleMonth);
    const last = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0);
    return {
      from: startOfDay(first).toISOString(),
      to: endOfDay(last).toISOString(),
    };
  }, [visibleMonth]);

  const slotsQuery = trpc.scheduler.listSlotsForBlock.useQuery(
    {
      blockId: step.blockId,
      from: slotRange.from,
      to: slotRange.to,
      timezone,
    },
    {
      enabled: canFetch,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    }
  );

  const bookingMutation = trpc.scheduler.createBookingForBlock.useMutation();

  const slotsByDay = useMemo(() => {
    if (isPreviewDemo) {
      return buildPreviewSlotsByDay(visibleMonth, durationMinutes);
    }

    const map = new Map<string, { startTime: string; endTime: string }[]>();
    for (const slot of slotsQuery.data ?? []) {
      const d = new Date(slot.startTime);
      const key = toYmd(d);
      const arr = map.get(key) ?? [];
      arr.push(slot);
      map.set(key, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    }
    return map;
  }, [durationMinutes, isPreviewDemo, slotsQuery.data, visibleMonth]);

  const slotsForSelectedDay = useMemo(() => {
    if (!selectedDate) return [];
    return slotsByDay.get(toYmd(selectedDate)) ?? [];
  }, [slotsByDay, selectedDate]);

  const grid = useMemo(() => buildCalendarGrid(visibleMonth), [visibleMonth]);
  const today = useMemo(() => startOfDay(new Date()), []);
  const timezoneLabel = useMemo(() => formatTimezoneLabel(timezone), [timezone]);

  const canGoPrevMonth = startOfMonth(visibleMonth).getTime() > startOfMonth(today).getTime();

  const handlePrevMonth = () => {
    if (!canGoPrevMonth) return;
    setVisibleMonth((m) => addMonths(m, -1));
  };

  const handleNextMonth = () => {
    setVisibleMonth((m) => addMonths(m, 1));
  };

  const handlePickDay = (d: Date) => {
    if (d < today) return;
    setSelectedDate(d);
    setSelectedSlot(null);
    setError(null);
    setView("time");
  };

  const handlePickSlot = (slot: { startTime: string; endTime: string }) => {
    setSelectedSlot(slot);
    setError(null);
    setView("details");
  };

  const handleBackToDate = () => {
    setView("date");
    setSelectedSlot(null);
    setError(null);
  };

  const handleBackToTime = () => {
    setView("time");
    setError(null);
  };

  const handleConfirm = async () => {
    if (!selectedSlot) return;
    if (!inviteeName.trim() || !inviteeEmail.trim()) {
      setError("Add your name and email to finish booking.");
      return;
    }
    setError(null);

    if (isPreviewDemo) {
      const value: BookingResponseValue = {
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        inviteeName: inviteeName.trim(),
        inviteeEmail: inviteeEmail.trim(),
        externalId: "preview:demo",
        externalUrl: null,
        meetUrl: null,
      };
      setBooking(value);
      return;
    }

    try {
      const result = await bookingMutation.mutateAsync({
        blockId: step.blockId,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        inviteeName: inviteeName.trim(),
        inviteeEmail: inviteeEmail.trim(),
        timezone,
      });
      const value: BookingResponseValue = {
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        inviteeName: inviteeName.trim(),
        inviteeEmail: inviteeEmail.trim(),
        externalId: result.externalId,
        externalUrl: result.externalUrl,
        meetUrl: result.meetUrl,
      };
      setBooking(value);
      form.setValue(step.blockId, JSON.stringify(value), { shouldValidate: true, shouldDirty: true });
      if (!result.externalId.startsWith("pending:")) {
        window.setTimeout(() => handleAnswer?.(), 200);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Something went wrong. Try another time.";
      setError(msg);
    }
  };

  const showInitialLoading = !isPreviewDemo && canFetch && slotsQuery.isLoading && !slotsQuery.data;
  const settingsMissing = !isPreviewDemo && (!settings || !settings.connectionId || !settings.calendarId);

  if (settingsMissing) {
    return (
      <div className="unconfiguredNotice">
        Connect a calendar in <strong>Setup</strong> to enable booking for this question.
      </div>
    );
  }

  if (booking) {
    return (
      <BookingConfirmationCard
        startTime={booking.startTime}
        endTime={booking.endTime}
        inviteeName={booking.inviteeName}
        inviteeEmail={booking.inviteeEmail}
        meetUrl={booking.meetUrl}
        externalUrl={booking.externalUrl}
        isPending={booking.externalId.startsWith("pending:")}
      />
    );
  }

  return (
    <div className="schedulerEmbedStack">
      <div className="embedFrame embedFrame--ready">
        <div className="googleBooker">
          {showInitialLoading && <EmbedLoadingSkeleton />}

          <aside className="googleBooker__event">
            <h4 className="googleBooker__eventTitle">{eventTitle}</h4>
            <p className="googleBooker__eventMeta">
              {durationMinutes}m · {timezoneLabel}
            </p>
          </aside>

          <div className="googleBooker__main">
            {view === "date" && (
              <>
                <p className="googleBooker__stepLabel">Select a date</p>
                <div className="googleBooker__calendar" aria-label="Choose a date">
                  <div className="googleBooker__calendarNav">
                    <button
                      type="button"
                      className="googleBooker__navBtn"
                      aria-label="Previous month"
                      onClick={handlePrevMonth}
                      disabled={!canGoPrevMonth}
                    >
                      <Icon name="chevron" className="googleBooker__navIcon googleBooker__navIcon--prev" />
                    </button>
                    <span className="googleBooker__monthLabel">
                      {MONTH_LABELS[visibleMonth.getMonth()]} {visibleMonth.getFullYear()}
                    </span>
                    <button
                      type="button"
                      className="googleBooker__navBtn"
                      aria-label="Next month"
                      onClick={handleNextMonth}
                    >
                      <Icon name="chevron" className="googleBooker__navIcon googleBooker__navIcon--next" />
                    </button>
                  </div>

                  <div className="googleBooker__weekRow">
                    {WEEKDAY_LABELS.map((w) => (
                      <span key={w}>{w}</span>
                    ))}
                  </div>

                  <div className="googleBooker__dayGrid">
                    {grid.map((d) => {
                      const inMonth = d.getMonth() === visibleMonth.getMonth();
                      const isPast = d < today;
                      const hasSlots = slotsByDay.has(toYmd(d));
                      const enabled =
                        !isPast && inMonth && (isRuntime ? hasSlots || !slotsQuery.data : true);
                      const isToday = sameDay(d, today);
                      const isSelected = !!selectedDate && sameDay(d, selectedDate);
                      return (
                        <button
                          key={d.toISOString()}
                          type="button"
                          onClick={() => enabled && handlePickDay(d)}
                          className={cn(
                            "googleBooker__day",
                            !inMonth && "googleBooker__day--outside",
                            !enabled && "googleBooker__day--disabled",
                            isToday && "googleBooker__day--today",
                            hasSlots && inMonth && !isPast && "googleBooker__day--available",
                            isSelected && "googleBooker__day--selected"
                          )}
                          disabled={!enabled}
                          aria-label={d.toLocaleDateString(undefined, {
                            weekday: "long",
                            month: "long",
                            day: "numeric",
                          })}
                        >
                          <span className="googleBooker__dayNumber">{d.getDate()}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {view === "time" && selectedDate && (
              <>
                <button type="button" className="googleBooker__backBtn" onClick={handleBackToDate}>
                  <Icon name="chevron" className="googleBooker__navIcon googleBooker__navIcon--prev" />
                  <span>Back</span>
                </button>

                <p className="googleBooker__stepLabel">{formatDateHeading(selectedDate)}</p>

                <div className="googleBooker__timesList" aria-label="Choose a time">
                  {slotsQuery.isLoading && <div className="googleBooker__empty">Loading times…</div>}
                  {!slotsQuery.isLoading && slotsForSelectedDay.length === 0 && (
                    <div className="googleBooker__empty">No open times this day. Try another date.</div>
                  )}
                  {!slotsQuery.isLoading &&
                    slotsForSelectedDay.map((slot) => (
                      <button
                        key={slot.startTime}
                        type="button"
                        className="googleBooker__timeSlot"
                        onClick={() => handlePickSlot(slot)}
                      >
                        {formatSlotTime(slot.startTime)}
                      </button>
                    ))}
                </div>
              </>
            )}

            {view === "details" && selectedSlot && (
              <>
                <button type="button" className="googleBooker__backBtn" onClick={handleBackToTime}>
                  <Icon name="chevron" className="googleBooker__navIcon googleBooker__navIcon--prev" />
                  <span>Back</span>
                </button>

                <p className="googleBooker__stepLabel">Enter your details</p>

                <div className="googleBooker__bookingSummary">
                  {formatDateHeading(new Date(selectedSlot.startTime))} ·{" "}
                  {formatSlotTime(selectedSlot.startTime)} · {durationMinutes}m · {timezoneLabel}
                </div>

                <div className="googleBooker__fields">
                  <label className="googleBooker__field" htmlFor={`${step.blockId}-name`}>
                    <span className="googleBooker__fieldLabel">Your name</span>
                    <input
                      id={`${step.blockId}-name`}
                      type="text"
                      placeholder="Jane Doe"
                      value={inviteeName}
                      onChange={(e) => setInviteeName(e.target.value)}
                      autoComplete="name"
                    />
                  </label>
                  <label className="googleBooker__field" htmlFor={`${step.blockId}-email`}>
                    <span className="googleBooker__fieldLabel">Email address</span>
                    <input
                      id={`${step.blockId}-email`}
                      type="email"
                      placeholder="you@example.com"
                      value={inviteeEmail}
                      onChange={(e) => setInviteeEmail(e.target.value)}
                      autoComplete="email"
                    />
                  </label>
                </div>

                {error && <p className="googleBooker__error">{error}</p>}

                <button
                  type="button"
                  className="googleBooker__submit"
                  onClick={handleConfirm}
                  disabled={bookingMutation.isPending}
                >
                  {bookingMutation.isPending ? "Confirming…" : "Confirm"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomCalendarSlot;
